import { useLoaderData, useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExtractedImagesState, useMatchToOEM, useUploadedImages } from '../../hooks';
import { ConfirmationModal, LoadingSpinner, PageBody, PageHeader, PageLayout } from "../../components";
import { ImageItemResult } from "../../features/ImageItemResult/ImageItemResult";
import { Button, Col, Row } from 'react-bootstrap';
import EmptyState from "../../components/EmptyState/EmptyState";
import { useImageMatcherFilter } from "../../hooks/useImageMatcherFilter";
import ImageMatcherActionCard from "../../features/ImageMatcherActionCard/ImageMatcherActionCard";
import { imageMatcherUploadPageURL } from '../../utils/imageMatcherUtil';
import { ManualDrawer } from '../../features';
import { set } from 'lodash';

const ImageMatcherResultPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const imageId = useLoaderData() as string;
    const { isDisplayBack } = useUploadedImages();


    const { matchOemToSingleClick, unmatchOemClick } = useMatchToOEM();
    const {
        previewImage,
        pineconeMatches,
        isLoading,
        getCurrentUploadedImage,
        selectedNameSpace,
        setSelectedNameSpace,
        selectedItemGroups,
        setSelectedItemGroups,
        selectedManufacturers,
        setSelectedManufacturers,
        imageFileObject,
        setMetadata,
        metadata,
        handleApplyFilter,
        isModalOpen,
        confirmNavigation,
        cancelNavigation,
    } = useImageMatcherFilter();

    const { setDisplayImageManuals, selectedImages, displayImageManuals } = useExtractedImagesState();
    const [checked, setChecked] = useState<boolean[]>(new Array(pineconeMatches.length).fill(false));

    useEffect(() => {
        getCurrentUploadedImage(imageId);
    }, [imageId]);

    useEffect(() => {
        if (previewImage) {
            handleApplyFilter(imageId);
        }
    }, [selectedItemGroups, selectedManufacturers, selectedNameSpace, metadata])

    useEffect(() => {
        const isDisplayManualsDetail = selectedImages.length > 0;
        setDisplayImageManuals(isDisplayManualsDetail);
        if (!isDisplayManualsDetail) {
            setChecked(new Array(pineconeMatches.length).fill(false))
        }
    }, [selectedImages, setDisplayImageManuals, displayImageManuals, pineconeMatches]);



    const renderImages = () => {
        if (isLoading) return <LoadingSpinner t={t} />;
        if (pineconeMatches.length === 0) {
            return <EmptyState message={t("Filter.resultsNotFound")} />;
        }
        return pineconeMatches.map((match, index) => (
            <ImageItemResult
                key={index}
                index={index}
                score={match.score}
                metadata={match.metadata}
                elements={match.elements}
                onMatchClick={() => matchOemToSingleClick(match.metadata, imageId, selectedNameSpace)}
                onUnmatchClick={() => unmatchOemClick(match.metadata, selectedNameSpace)}
                isDisplayConfig
                checked={checked}
                setChecked={setChecked}
            />
        ));
    };

    return (
        <PageLayout>
            <ConfirmationModal
                show={isModalOpen}
                title={t("ConfirmationModal.ConfirmationTitle")}
                body={t("ConfirmationModal.ConfirmationMessage")}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation}
            />
            <PageHeader
                title={t('ExtractedImages.ImageMatcher')}
                t={t}
                isDisplayBack={isDisplayBack()}
                onBackClick={() => navigate(imageMatcherUploadPageURL)}
            />
            <PageBody t={t}>
                <Row className="p-3">
                    <Col md={8}
                        className="d-flex flex-wrap gap-3 match-images-container justify-content-center mb-3">
                        {renderImages()}
                    </Col>
                    <Col md={4} className="d-flex flex-column gap-3 p-2">
                        <img
                            src={previewImage}
                            alt={imageId}
                            className='rounded mx-auto d-block image-size'
                        />
                        <ImageMatcherActionCard
                            isLoading={isLoading || checked.some(Boolean)}
                            selectedNameSpace={selectedNameSpace}
                            setSelectedNameSpace={setSelectedNameSpace}
                            selectedItemGroups={selectedItemGroups}
                            setSelectedItemGroups={setSelectedItemGroups}
                            selectedManufacturers={selectedManufacturers}
                            setSelectedManufacturers={setSelectedManufacturers}
                            imageFileObject={imageFileObject}
                            pineconeMatches={pineconeMatches}
                            setMetadata={setMetadata}
                            imageId={imageId}
                        />
                        {<ManualDrawer forceFeedback={false} />}
                    </Col>
                </Row>
            </PageBody>
        </PageLayout>
    );
};

export default ImageMatcherResultPage;
